import * as React from 'react';
import {
    Typography,
    Box,
    Container,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Chip,
    Alert,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { main_button, sec_button } from '../../app/components/Styles';
import { Home, List, ArrowBack } from '@mui/icons-material';
import { useEffect, useCallback } from 'react';

const STATUS_COLORS = {
    pending: 'warning',
    processing: 'warning',
    success: 'success',
    failed: 'error',
    skipped: 'default',
};

const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1);
};

export const ItemUpdateProgress = ({
    getItemUpdateTask,
    downloadOutboundReceipt,
    downloadInboundReceipt,
    historyPath,
    homePath,
    inboundsPath,
}) => {
    const navigate = useNavigate();
    const { taskId } = useParams();
    const [task, setTask] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [pollError, setPollError] = React.useState(null);

    const fetchTask = useCallback(async () => {
        try {
            const data = await getItemUpdateTask(taskId);
            setTask(data);
            setPollError(null);
        } catch (err) {
            setPollError(err?.response?.data?.message || 'Failed to load item update progress.');
        } finally {
            setIsLoading(false);
        }
    }, [getItemUpdateTask, taskId]);

    useEffect(() => {
        fetchTask();
    }, [fetchTask]);

    useEffect(() => {
        if (!task || task.status !== 'processing') {
            return undefined;
        }
        const interval = setInterval(fetchTask, 3000);
        return () => clearInterval(interval);
    }, [task?.status, fetchTask]);

    const summary = task?.summary || {};
    const items = task?.items || [];
    const completedCount = (summary.success || 0) + (summary.failed || 0) + (summary.skipped || 0);
    const progressPercent = summary.total > 0 ? Math.round((completedCount / summary.total) * 100) : 0;
    const title = task?.kind === 'outbound' ? 'Outbound update' : 'Inbound receive';

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                <Typography variant="h4" textAlign="center" sx={{ color: 'var(--secColor)', mb: 1 }}>
                    {title}
                </Typography>
                <Typography variant="body2" textAlign="center" color="text.secondary" sx={{ mb: 2 }}>
                    Each line is the success or failure of that stock update.
                </Typography>

                {isLoading && !task ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : null}

                {pollError && <Alert severity="error" sx={{ mb: 2 }}>{pollError}</Alert>}

                {task?.status === 'processing' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Stock updates are running in the background.
                    </Alert>
                )}
                {task?.status === 'completed' && (
                    <Alert severity={summary.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                        {summary.failed > 0
                            ? `${summary.success || 0} updated, ${summary.failed} failed.`
                            : 'Every line finished.'}
                    </Alert>
                )}
                {task?.status === 'failed' && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        The update stopped. Lines that were not applied are skipped.
                    </Alert>
                )}

                {task && (
                    <>
                        <LinearProgress variant="determinate" value={task.status === 'processing' ? progressPercent : 100} sx={{ mb: 2 }} />
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Brand</TableCell>
                                        <TableCell>SKU</TableCell>
                                        <TableCell>Product</TableCell>
                                        <TableCell>Qty</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Error</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item, index) => (
                                        <TableRow key={`${item.sku || 'line'}-${index}`}>
                                            <TableCell>{item.brandName || '—'}</TableCell>
                                            <TableCell>{item.sku || '—'}</TableCell>
                                            <TableCell>{item.name || '—'}</TableCell>
                                            <TableCell>{item.applyQuantity ?? item.quantity ?? '—'}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={formatStatus(item.status)} color={STATUS_COLORS[item.status] || 'default'} />
                                            </TableCell>
                                            <TableCell>{item.error || ''}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
                            {task.kind === 'inbound' && task.status !== 'processing' && inboundsPath && (
                                <Button sx={main_button} startIcon={<ArrowBack />} onClick={() => navigate(inboundsPath)}>
                                    Back to inbounds
                                </Button>
                            )}
                            {task.kind === 'outbound' && task.outbound && task.status !== 'processing' && (
                                <Button sx={main_button} onClick={() => downloadOutboundReceipt(task.outbound)}>
                                    Outbound receipt
                                </Button>
                            )}
                            {(task.receiptGroups || []).map((group) => (
                                <Button
                                    key={`${group.prRef || ''}-${group.inboundId || ''}`}
                                    sx={main_button}
                                    onClick={() => downloadInboundReceipt(group.prRef ? { prRef: group.prRef } : { inboundId: group.inboundId })}
                                >
                                    Receipt {group.brandName || ''} {group.prRef ? `(${group.prRef})` : ''}
                                </Button>
                            ))}
                            <Button sx={sec_button} startIcon={<List />} onClick={() => navigate(historyPath)}>History</Button>
                            <Button sx={sec_button} startIcon={<Home />} onClick={() => navigate(homePath)}>Home</Button>
                        </Box>
                    </>
                )}
            </Paper>
        </Container>
    );
};
